<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";
import TraderSidebar from "@/components/trader/TraderSidebar.vue";

const { t } = useI18n();

const router = useRouter();

const stats = ref({
  portfolioValue: "$89,450",
  totalCredits: "1,247",
  creditsRetired: "423",
  activeOrders: 8,
});

const recentTransactions = ref([
  {
    id: "TXN-001",
    type: "Purchase",
    project: "Solar Farm Alpha",
    credits: 150,
    price: "$12.50",
    date: "2024-01-15",
    status: "Completed",
  },
  {
    id: "TXN-002",
    type: "Sale",
    project: "Wind Energy Beta",
    credits: 200,
    price: "$13.75",
    date: "2024-01-14",
    status: "Pending",
  },
  {
    id: "TXN-003",
    type: "Retirement",
    project: "Reforestation Gamma",
    credits: 75,
    price: "$11.20",
    date: "2024-01-12",
    status: "Completed",
  },
]);

const marketPrices = ref([
  { type: "Solar", price: 12.45, change: "+2.1%", trend: "up" },
  { type: "Wind", price: 13.8, change: "-0.8%", trend: "down" },
  { type: "Forestry", price: 11.9, change: "+1.5%", trend: "up" },
  { type: "Biomass", price: 10.75, change: "+0.3%", trend: "up" },
]);
</script>

<template>
  <div class="flex h-screen bg-gray-50">
    <TraderSidebar />

    <div class="flex-1 flex flex-col overflow-hidden">
      <MemberHeader />

      <main id="main-dashboard" class="flex-1 p-6">
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-accent mb-2">
            {{ $t('trader.dashboard_page.title') }}
          </h1>
          <p class="text-gray-600">
            {{ $t('trader.dashboard_page.subtitle') }}
          </p>
        </div>

        <div
          id="stats-overview"
          class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">
                  {{ $t('trader.dashboard_page.stats.total_credits_owned') }}
                </p>
                <p class="text-2xl font-bold text-accent">12,847</p>
                <p class="text-xs text-green-600">+247 {{ $t('trader.dashboard_page.stats.this_month') }}</p>
              </div>
              <div
                class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-coins text-primary text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">{{ $t('trader.dashboard_page.stats.credits_retired') }}</p>
                <p class="text-2xl font-bold text-accent">3,245</p>
                <p class="text-xs text-blue-600">+89 {{ $t('trader.dashboard_page.stats.this_month') }}</p>
              </div>
              <div
                class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-fire text-secondary text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">{{ $t('trader.dashboard_page.stats.portfolio_value') }}</p>
                <p class="text-2xl font-bold text-accent">$384,705</p>
                <p class="text-xs text-green-600">+5.2% {{ $t('trader.dashboard_page.stats.this_month') }}</p>
              </div>
              <div
                class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-dollar-sign text-accent text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">
                  {{ $t('trader.dashboard_page.stats.active_transactions') }}
                </p>
                <p class="text-2xl font-bold text-accent">7</p>
                <p class="text-xs text-yellow-600">2 {{ $t('trader.dashboard_page.stats.pending') }}</p>
              </div>
              <div
                class="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-clock text-yellow-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div
            id="recent-transactions"
            class="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6"
          >
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-lg font-semibold text-accent">
                {{ $t('trader.dashboard_page.recent_transactions.title') }}
              </h2>
              <button class="text-primary text-sm font-medium hover:underline">
                {{ $t('trader.dashboard_page.recent_transactions.view_all') }}
              </button>
            </div>

            <div class="space-y-4">
              <div
                class="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
              >
                <div class="flex items-center space-x-3">
                  <div
                    class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"
                  >
                    <i class="fas fa-plus text-green-600"></i>
                  </div>
                  <div>
                    <p class="font-medium text-accent">{{ $t('trader.dashboard_page.recent_transactions.purchased_credits') }}</p>
                    <p class="text-sm text-gray-600">
                      {{ $t('trader.dashboard_page.recent_transactions.project') }}: Solar Farm Indonesia #SOL-2024-001
                    </p>
                    <p class="text-xs text-gray-500">Dec 15, 2024 at 2:34 PM</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="font-semibold text-green-600">+500 Credits</p>
                  <p class="text-sm text-gray-600">$15,000</p>
                </div>
              </div>

              <div
                class="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
              >
                <div class="flex items-center space-x-3">
                  <div
                    class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"
                  >
                    <i class="fas fa-fire text-blue-600"></i>
                  </div>
                  <div>
                    <p class="font-medium text-accent">{{ $t('trader.dashboard_page.recent_transactions.retired_credits') }}</p>
                    <p class="text-sm text-gray-600">
                      {{ $t('trader.dashboard_page.recent_transactions.project') }}: Wind Farm Brazil #WND-2023-045
                    </p>
                    <p class="text-xs text-gray-500">
                      Dec 12, 2024 at 11:22 AM
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="font-semibold text-blue-600">-200 Credits</p>
                  <p class="text-sm text-gray-600">{{ $t('trader.dashboard_page.recent_transactions.retired') }}</p>
                </div>
              </div>

              <div
                class="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
              >
                <div class="flex items-center space-x-3">
                  <div
                    class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"
                  >
                    <i class="fas fa-exchange-alt text-orange-600"></i>
                  </div>
                  <div>
                    <p class="font-medium text-accent">{{ $t('trader.dashboard_page.recent_transactions.transferred_credits') }}</p>
                    <p class="text-sm text-gray-600">{{ $t('trader.dashboard_page.recent_transactions.to') }}: Green Corp Ltd.</p>
                    <p class="text-xs text-gray-500">Dec 10, 2024 at 4:15 PM</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="font-semibold text-orange-600">-750 Credits</p>
                  <p class="text-sm text-gray-600">$22,500</p>
                </div>
              </div>
            </div>
          </div>

          <div
            id="quick-actions"
            class="bg-white border border-gray-200 rounded-lg p-6"
          >
            <h2 class="text-lg font-semibold text-accent mb-6">
              {{ $t('trader.dashboard_page.quick_actions.title') }}
            </h2>

            <div class="space-y-4">
              <button
                class="w-full bg-primary text-white p-4 rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                <i class="fas fa-shopping-cart mr-2"></i>
                {{ $t('trader.dashboard_page.quick_actions.buy_credits') }}
              </button>

              <button
                class="w-full bg-secondary text-accent p-4 rounded-lg font-medium hover:bg-secondary/90 transition-colors"
              >
                <i class="fas fa-exchange-alt mr-2"></i>
                {{ $t('trader.dashboard_page.quick_actions.transfer_credits') }}
              </button>

              <button
                class="w-full bg-accent text-white p-4 rounded-lg font-medium hover:bg-accent/90 transition-colors"
              >
                <i class="fas fa-fire mr-2"></i>
                {{ $t('trader.dashboard_page.quick_actions.retire_credits') }}
              </button>

              <button
                class="w-full border border-gray-300 text-gray-700 p-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <i class="fas fa-download mr-2"></i>
                {{ $t('trader.dashboard_page.quick_actions.export_ledger') }}
              </button>
            </div>

            <div
              class="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg"
            >
              <div class="flex items-center space-x-2 mb-2">
                <i class="fas fa-exclamation-triangle text-amber-600"></i>
                <span class="text-sm font-medium text-amber-800"
                  >{{ $t('trader.dashboard_page.quick_actions.market_alert') }}</span
                >
              </div>
              <p class="text-xs text-amber-700">
                {{ $t('trader.dashboard_page.quick_actions.market_alert_message') }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            id="portfolio-breakdown"
            class="bg-white border border-gray-200 rounded-lg p-6"
          >
            <h2 class="text-lg font-semibold text-accent mb-6">
              {{ $t('trader.dashboard_page.portfolio_breakdown.title') }}
            </h2>

            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span class="text-sm font-medium">{{ $t('trader.dashboard_page.portfolio_breakdown.renewable_energy') }}</span>
                </div>
                <div class="text-right">
                  <span class="text-sm font-semibold">5,234 {{ $t('trader.dashboard_page.portfolio_breakdown.credits') }}</span>
                  <span class="text-xs text-gray-500 block">40.7%</span>
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-blue-500 rounded-full"></div>
                  <span class="text-sm font-medium">{{ $t('trader.dashboard_page.portfolio_breakdown.forestry_land_use') }}</span>
                </div>
                <div class="text-right">
                  <span class="text-sm font-semibold">4,156 {{ $t('trader.dashboard_page.portfolio_breakdown.credits') }}</span>
                  <span class="text-xs text-gray-500 block">32.3%</span>
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-purple-500 rounded-full"></div>
                  <span class="text-sm font-medium">{{ $t('trader.dashboard_page.portfolio_breakdown.energy_efficiency') }}</span>
                </div>
                <div class="text-right">
                  <span class="text-sm font-semibold">2,789 {{ $t('trader.dashboard_page.portfolio_breakdown.credits') }}</span>
                  <span class="text-xs text-gray-500 block">21.7%</span>
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span class="text-sm font-medium">{{ $t('trader.dashboard_page.portfolio_breakdown.other') }}</span>
                </div>
                <div class="text-right">
                  <span class="text-sm font-semibold">668 {{ $t('trader.dashboard_page.portfolio_breakdown.credits') }}</span>
                  <span class="text-xs text-gray-500 block">5.2%</span>
                </div>
              </div>
            </div>
          </div>

          <div
            id="market-insights"
            class="bg-white border border-gray-200 rounded-lg p-6"
          >
            <h2 class="text-lg font-semibold text-accent mb-6">
              {{ $t('trader.dashboard_page.market_insights.title') }}
            </h2>

            <div class="space-y-4">
              <div class="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-green-800"
                    >{{ $t('trader.dashboard_page.market_insights.average_credit_price') }}</span
                  >
                  <span class="text-lg font-bold text-green-600">$29.50</span>
                </div>
                <p class="text-xs text-green-700">+2.3% {{ $t('trader.dashboard_page.market_insights.from_last_week') }}</p>
              </div>

              <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-blue-800"
                    >{{ $t('trader.dashboard_page.market_insights.trading_volume') }}</span
                  >
                  <span class="text-lg font-bold text-blue-600">847K</span>
                </div>
                <p class="text-xs text-blue-700">{{ $t('trader.dashboard_page.market_insights.credits_traded_this_week') }}</p>
              </div>

              <div class="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-purple-800"
                    >{{ $t('trader.dashboard_page.market_insights.new_projects') }}</span
                  >
                  <span class="text-lg font-bold text-purple-600">23</span>
                </div>
                <p class="text-xs text-purple-700">{{ $t('trader.dashboard_page.market_insights.registered_this_month') }}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
